import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { blogPosts } from '../data/blogPosts';
import { ArrowRight, Clock, Tag } from 'lucide-react';

const blogSchema = {
  '@context': 'https://schema.org',
  '@type': 'Blog',
  name: 'RELOAD Nutrition Blog',
  description: 'Expert guides on male reproductive health, semen volume, libido, and natural supplementation.',
  url: 'https://reload-nutrition.com/blog',
  publisher: {
    '@type': 'Organization',
    name: 'RELOAD Nutrition',
    url: 'https://reload-nutrition.com',
  },
};

export default function Blog() {
  return (
    <>
      <SEO
        title="Blog — Male Health, Libido & Semen Volume Guides"
        description="Expert articles on how to increase semen volume, boost libido naturally, and optimise male reproductive health. Science-backed guides from RELOAD Nutrition."
        canonical="/blog"
        schema={blogSchema}
      />

      {/* Hero */}
      <section
        data-testid="blog-hero"
        style={{ backgroundColor: '#1B3D7E', paddingTop: '80px', paddingBottom: '80px' }}>
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <span style={{
            display: 'inline-block',
            backgroundColor: 'rgba(255,255,255,0.12)',
            color: '#FFFFFF',
            fontSize: '0.68rem',
            letterSpacing: '0.25em',
            textTransform: 'uppercase',
            fontFamily: 'Manrope, sans-serif',
            fontWeight: 700,
            padding: '6px 16px',
            borderRadius: '100px',
            marginBottom: '20px',
          }}>
            Knowledge Hub
          </span>
          <h1 style={{
            fontFamily: 'Outfit, sans-serif',
            fontWeight: 800,
            fontSize: 'clamp(2rem, 5vw, 3.2rem)',
            color: '#FFFFFF',
            lineHeight: 1.15,
            marginBottom: '16px',
          }}>
            Male Health &amp; Performance
          </h1>
          <p style={{
            fontFamily: 'Manrope, sans-serif',
            fontSize: '1.05rem',
            color: 'rgba(255,255,255,0.75)',
            lineHeight: 1.7,
            maxWidth: '560px',
            margin: '0 auto',
          }}>
            Science-backed guides on semen volume, libido, and male reproductive vitality — written to help you make informed decisions.
          </p>
        </div>
      </section>

      {/* Articles Grid */}
      <section
        data-testid="blog-articles-grid"
        style={{ backgroundColor: '#F7F7F5', padding: '80px 0 100px' }}>
        <div className="max-w-6xl mx-auto px-6 md:px-12">

          {/* Featured (first article) */}
          <div className="mb-14">
            <h2 style={{
              fontFamily: 'Outfit, sans-serif',
              fontSize: '0.68rem',
              letterSpacing: '0.25em',
              textTransform: 'uppercase',
              color: '#1B3D7E',
              fontWeight: 700,
              marginBottom: '24px',
            }}>
              Featured Article
            </h2>
            <Link
              to={`/blog/${blogPosts[0].slug}`}
              data-testid={`blog-card-featured-${blogPosts[0].slug}`}
              style={{ textDecoration: 'none', display: 'block' }}>
              <div
                className="group"
                style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  border: '1px solid #E2E8F0',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                  transition: 'box-shadow 0.25s, transform 0.25s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 12px 40px rgba(27,61,126,0.12)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                <div style={{ overflow: 'hidden', maxHeight: '340px', minHeight: '220px' }}>
                  <img
                    src={blogPosts[0].image}
                    alt={blogPosts[0].title}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.4s' }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.03)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  />
                </div>
                <div style={{ padding: '40px 36px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                    <span style={{ backgroundColor: '#EEF2FF', color: '#1B3D7E', fontSize: '0.68rem', fontFamily: 'Manrope, sans-serif', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', padding: '4px 10px', borderRadius: '100px' }}>
                      {blogPosts[0].category}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#94A3B8', fontSize: '0.75rem', fontFamily: 'Manrope, sans-serif' }}>
                      <Clock size={12} />
                      {blogPosts[0].readTime}
                    </span>
                  </div>
                  <h3 style={{
                    fontFamily: 'Outfit, sans-serif',
                    fontWeight: 800,
                    fontSize: 'clamp(1.2rem, 2.5vw, 1.7rem)',
                    color: '#0D1A2E',
                    lineHeight: 1.25,
                    marginBottom: '14px',
                  }}>
                    {blogPosts[0].title}
                  </h3>
                  <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '0.9rem', color: '#64748B', lineHeight: 1.7, marginBottom: '24px' }}>
                    {blogPosts[0].excerpt}
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#1B3D7E', fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: '0.82rem', letterSpacing: '0.05em' }}>
                    Read Article <ArrowRight size={14} />
                  </div>
                </div>
              </div>
            </Link>
          </div>

          {/* Other articles */}
          <h2 style={{
            fontFamily: 'Outfit, sans-serif',
            fontSize: '0.68rem',
            letterSpacing: '0.25em',
            textTransform: 'uppercase',
            color: '#1B3D7E',
            fontWeight: 700,
            marginBottom: '24px',
          }}>
            More Articles
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {blogPosts.slice(1).map((post) => (
              <Link
                key={post.slug}
                to={`/blog/${post.slug}`}
                data-testid={`blog-card-${post.slug}`}
                style={{ textDecoration: 'none' }}>
                <div
                  style={{
                    backgroundColor: '#FFFFFF',
                    borderRadius: '14px',
                    overflow: 'hidden',
                    border: '1px solid #E2E8F0',
                    transition: 'box-shadow 0.25s, transform 0.25s',
                    height: '100%',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 10px 32px rgba(27,61,126,0.10)'; e.currentTarget.style.transform = 'translateY(-3px)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                  <div style={{ height: '200px', overflow: 'hidden' }}>
                    <img
                      src={post.image}
                      alt={post.title}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.4s' }}
                      onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.04)'}
                      onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                    />
                  </div>
                  <div style={{ padding: '24px 28px 28px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                      <span style={{ backgroundColor: '#EEF2FF', color: '#1B3D7E', fontSize: '0.65rem', fontFamily: 'Manrope, sans-serif', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', padding: '3px 9px', borderRadius: '100px' }}>
                        {post.category}
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#94A3B8', fontSize: '0.72rem', fontFamily: 'Manrope, sans-serif' }}>
                        <Clock size={11} />
                        {post.readTime}
                      </span>
                    </div>
                    <h3 style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 800, fontSize: '1.1rem', color: '#0D1A2E', lineHeight: 1.3, marginBottom: '10px' }}>
                      {post.title}
                    </h3>
                    <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '0.84rem', color: '#64748B', lineHeight: 1.65, marginBottom: '18px' }}>
                      {post.excerpt}
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#1B3D7E', fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: '0.78rem' }}>
                      Read Article <ArrowRight size={13} />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
